import { httpCart } from "@white-label-configuration/http"

import { AttachmentType } from '@white-label-configuration/constants';
import { datadogRum } from "@datadog/browser-rum";
import { parseCartResponse } from '@white-label-helper/data-parsers-cart';

import type { BookingFee, SearchProducts } from '@white-label-types/parking-booking';
import { Cart } from '@white-label-types/parking-checkout';

import mockCartData from './mocks/mock-cart-data.json';

type Meta = {
    original_order_item_id?: number | string
    original_order_item_total_price?: number
    pii_data_token?: string
    filter?: {
        index: number,
        item_list_name: string,
    },
}

export interface AddToCartData {
    cartToken: string;
    searchProductResultId: string;
    inventoryOptionId: number;
    searchToken?: string;
    couponCode?: string | null;
    originalOrderItemIsProtected?: boolean;
    language?: string;
    meta?: Meta;
    subOptionId?: string;
    subOptionIds?: string[];
    searchProduct?: SearchProducts;
    bundleId?: string;
    attachmentType?: AttachmentType;
}

interface AddToCartBody {
    search_product_result_id: string;
    inventory_option_id: number;
    customer_id: null;
    search_token?: string;
    original_order_item_is_protected?: boolean;
    coupon_code?: string;
    language?: string;
    meta?: Meta;
    sub_option_id?: number | string;
    sub_option_ids?: string[];
    bundle_id?: string;
    attachment_type?: AttachmentType;
}

interface UpdateCancellationProtectionBody {
    is_protected: boolean
}

// @ts-expect-error property
httpCart.onRequest((config) => {
    const session_id = datadogRum.getInternalContext()?.session_id || null;
    config.headers['X-Datadog-Session-ID'] = session_id;
});

export const fetchMockCartItemsWithTravelInsurance = async (cartToken: string) => {
    const cart = await fetchCartItems(cartToken);
    cart.items.push(mockCartData.travelInsuranceCartItem);
    return cart;
}

export const fetchCartItems = (cartToken: string): Promise<Cart> => {
    //cache buster to prevent browser from serving stale cart data on browser back navigation.
    //it adds cache-busting query parameter (`_cb=timestamp`) to the URL to force browser to fetch fresh data
    const cacheBuster = `_cb=${new Date().getTime()}`;
    const urlWithCacheBuster = `public/cart/${cartToken}?${cacheBuster}`;

    return httpCart.get(urlWithCacheBuster)
        .then(({ body }) => {
            return body.data;
        })
        .catch(error => {
            throw error;
        });
};

export const fetchCart = <I, T, P = unknown>(cartToken: string): Promise<{ items: I[], item: I, totals: T, booking_fee?: BookingFee, payable: P }> => (
    httpCart.get(`public/cart/${cartToken}`).then((res) => parseCartResponse<I, T, P>(res))
)
export const addToCartRequest = (cartToken: string, body: AddToCartBody) => (
    httpCart.post(`public/cart/${cartToken}/items`, { body })
);

export const addToCart = <I, T, P = unknown>({
    cartToken,
    searchProductResultId,
    inventoryOptionId,
    originalOrderItemIsProtected,
    meta,
    couponCode,
    searchToken,
    language,
    subOptionId,
    subOptionIds,
    bundleId,
    attachmentType,
}: AddToCartData): Promise<{ items: I[], item: I, totals: T, booking_fee?: BookingFee, payable: P }> => {
    const body: AddToCartBody = {
        search_product_result_id: searchProductResultId,
        inventory_option_id: inventoryOptionId,
        customer_id: null,
        search_token: searchToken,
        language,
    };
    if (originalOrderItemIsProtected) {
        body.original_order_item_is_protected = originalOrderItemIsProtected;
    }
    if (couponCode) {
        body.coupon_code = couponCode;
    }
    if (meta) {
        body.meta = { ...meta };
    }
    if (subOptionId) {
        body.sub_option_id = subOptionId;
    }
    if (subOptionIds) {
        body.sub_option_ids = subOptionIds;
    }
    if (bundleId) {
        body.bundle_id = bundleId;
    }
    if (attachmentType) {
        body.attachment_type = attachmentType;
    }
    return httpCart.post(`public/cart/${cartToken}/items`, { body }).then((res) => parseCartResponse<I, T, P>(res));
};

export const deleteCartItem = <I, T, P>(
    cartToken: string,
    itemId: string,
): Promise<{ items: I[], item: I, totals: T, payable: P }> => httpCart.delete(`/public/cart/${cartToken}/items/${itemId}`).then((res) => parseCartResponse<I, T, P>(res));


export const updateCancellationProtectionRequest = <I, T, P>(
    cartId: string,
    cartToken: string,
    body: UpdateCancellationProtectionBody,
): Promise<{ items: I[], item: I, totals: T, payable: P }> => httpCart.patch(`/public/cart/${cartToken}/item/${cartId}/protection/update`, { body }).then((res) => parseCartResponse<I, T, P>(res));
